import React, { useState, FormEvent, ChangeEvent, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Navbar from 'components/Header/Navbar';
import Button from 'components/Button';
import { listingService } from 'api/services/ListingService';
import { topCategoryService } from 'api/services/TopCategoryService';
import { conditionService } from 'api/services/ConditionService';
import { warrantyService } from 'api/services/WarrantyService';
import { productTypeService } from 'api/services/ProductTypeService';
import { cityService } from 'api/services/CityService';
import { districtService } from 'api/services/DistrictService';
import { sortCities } from 'utils/sortCities';
import type { TopCategoryViewModel } from 'api/types/top-category';
import type { CityViewModel } from 'api/types/city';
import type { DistrictViewModel } from 'api/types/district';
import type { ConditionViewModel } from 'api/types/condition';
import type { WarrantyViewModel } from 'api/types/warranty';
import type { SpecConstraintViewModel } from 'api/types/product-type/SpecConstraintViewModel';
import type { ListingViewModel } from 'api/types/listings/ListingViewModel';
import { MediaService } from 'api/services/MediaService';
import SlidingCategoryNavigator from 'components/Filter/SlidingCategoryNavigator';
import SpecInlineFilterPanel from 'components/Filter/SpecInlineFilterPanel';
import type { ListingFilterState } from 'types/listingFilterState';
import { defaultListingFilterState } from 'types/listingFilterState';
import { normalizeSpecConstraintList } from 'utils/specConstraintUtils';

interface FormData {
  category: string;
  secondaryCategoryId: string;
  productTypeId: string;
  brand: string;
  cpuModel: string;
  ram: string;
  storage: string;
  gpu: string;
  price: string;
  title: string;
  description: string;
  condition: string;
  warranty: string;
  testedSystem: boolean;
  city: string;
  district: string;
  neighborhood: string;
  termsAccepted: boolean;
  images: FileList | null;
  showPhoneOnListing: boolean;
}

const LEGACY_SPEC_KEYS = ['Marka', 'İşlemci Modeli', 'RAM', 'Depolama', 'Ekran Kartı'];

function buildSpecValuesFromListing(
  specs: Record<string, string> | null | undefined,
  constraints: SpecConstraintViewModel[]
): Record<string, string> {
  if (!specs || constraints.length === 0) return {};
  const values: Record<string, string> = {};
  for (const constraint of constraints) {
    const raw = specs[constraint.key];
    if (raw?.trim()) values[constraint.key] = raw.trim();
  }
  return values;
}

const initialFormData: FormData = {
  category: '',
  secondaryCategoryId: '',
  productTypeId: '',
  brand: '',
  cpuModel: '',
  ram: '',
  storage: '',
  gpu: '',
  price: '',
  title: '',
  description: '',
  condition: '',
  warranty: '',
  testedSystem: false,
  city: '',
  district: '',
  neighborhood: '',
  termsAccepted: false,
  images: null,
  showPhoneOnListing: false,
};

const Ilan_duzenle: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [topCategories, setTopCategories] = useState<TopCategoryViewModel[]>([]);
  const [conditions, setConditions] = useState<ConditionViewModel[]>([]);
  const [warranties, setWarranties] = useState<WarrantyViewModel[]>([]);
  const [specConstraints, setSpecConstraints] = useState<SpecConstraintViewModel[]>([]);
  const [specValues, setSpecValues] = useState<Record<string, string>>({});
  const [cities, setCities] = useState<CityViewModel[]>([]);
  const [districts, setDistricts] = useState<DistrictViewModel[]>([]);
  const [userForm, setFormData] = useState<FormData>(initialFormData);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [removedImageUrls, setRemovedImageUrls] = useState<string[]>([]);
  const [propertySections, setPropertySections] = useState<Record<string, boolean>>({
    categories: true,
    specs: true,
    condition: true,
    warranty: true,
  });

  const togglePropertySection = (sectionKey: string) => {
    setPropertySections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const handleChange = (e: ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFormData((prev) => ({ ...prev, images: e.target.files }));
  };

  const handleRemoveExistingImage = (url: string) => {
    setRemovedImageUrls((prev) => (prev.includes(url) ? prev : [...prev, url]));
  };

  useEffect(() => {
    const cityId = userForm.city ? Number(userForm.city) : NaN;
    if (!userForm.city || Number.isNaN(cityId)) {
      setDistricts([]);
      return;
    }
    districtService.getAll({ cityId }).then((res) => {
      if (res.isSuccess && res.data) setDistricts(res.data);
    });
  }, [userForm.city]);

  useEffect(() => {
    if (!id) {
      setLoadError('İlan ID bulunamadı.');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [listingRes, tcRes, condRes, warrRes, citiesRes] = await Promise.all([
          listingService.getById(id),
          topCategoryService.getPopulated(),
          conditionService.getAll(),
          warrantyService.getAll(),
          cityService.getAll(),
        ]);

        if (cancelled) return;
        if (!listingRes.isSuccess || !listingRes.data) {
          setLoadError(listingRes.message ?? 'İlan yüklenemedi.');
          setLoading(false);
          return;
        }

        if (tcRes.isSuccess && tcRes.data) setTopCategories(tcRes.data);
        if (condRes.isSuccess && condRes.data) setConditions(condRes.data);
        if (warrRes.isSuccess && warrRes.data) setWarranties(warrRes.data);
        if (citiesRes.isSuccess && citiesRes.data) setCities(sortCities(citiesRes.data));

        const listing: ListingViewModel = listingRes.data;
        const pt = listing.productType;
        const sec = pt?.secondaryCategory;
        let topCategoryId: number | null | undefined = sec?.topCategoryId ?? sec?.topCategory?.id;
        const secondaryCategoryId = sec?.id ?? pt?.secondaryCategoryId;
        const productTypeId = listing.productTypeId ?? pt?.id;

        if (
          (topCategoryId == null || topCategoryId === undefined) &&
          secondaryCategoryId != null &&
          tcRes.isSuccess &&
          tcRes.data?.length
        ) {
          for (const tc of tcRes.data) {
            if (tc.secondaryCategories?.some((sc) => String(sc.id) === String(secondaryCategoryId))) {
              topCategoryId = tc.id;
              break;
            }
          }
        }

        const warrantyList = warrRes.isSuccess && warrRes.data ? warrRes.data : [];
        let warrantyOption = warrantyList.find((w) => {
          if (typeof w.value === 'boolean') return w.value === listing.hasWarranty;
          const v = String(w.value ?? '').toLowerCase();
          return listing.hasWarranty
            ? v === 'true' || v === 'yes' || v === '1'
            : v === 'false' || v === 'no' || v === '0';
        });
        if (!warrantyOption && warrantyList.length > 0) {
          const nameMatch = (name: string | null | undefined, hasWarranty: boolean) => {
            const n = String(name ?? '').toLowerCase();
            if (hasWarranty) return /garanti|var|evet/i.test(n);
            return /yok|garantisiz|hayır/i.test(n);
          };
          warrantyOption = listing.hasWarranty
            ? warrantyList.find((w) => nameMatch(w.name, true)) ?? warrantyList[0]
            : warrantyList.find((w) => nameMatch(w.name, false)) ?? warrantyList[0];
        }
        const warrantyId = warrantyOption ? String(warrantyOption.id) : '';

        const categoryValue =
          topCategoryId != null && topCategoryId !== undefined
            ? String(topCategoryId)
            : '';
        const secondaryValue =
          secondaryCategoryId != null && secondaryCategoryId !== undefined
            ? String(secondaryCategoryId)
            : '';
        const productTypeValue =
          productTypeId != null && productTypeId !== undefined
            ? String(productTypeId)
            : '';

        setFormData({
          ...initialFormData,
          category: categoryValue,
          secondaryCategoryId: secondaryValue,
          productTypeId: productTypeValue,
          brand: listing.specs?.Marka ?? '',
          cpuModel: listing.specs?.['İşlemci Modeli'] ?? '',
          ram: listing.specs?.RAM ?? '',
          storage: listing.specs?.Depolama ?? '',
          gpu: listing.specs?.['Ekran Kartı'] ?? '',
          price: String(listing.price ?? ''),
          title: listing.title ?? '',
          description: listing.description ?? '',
          condition: String(listing.conditionId ?? ''),
          warranty: warrantyId,
          testedSystem: listing.isTested ?? false,
          city: String(listing.cityId ?? ''),
          district: String(listing.districtId ?? ''),
          neighborhood: '',
          termsAccepted: true,
          images: null,
          showPhoneOnListing: listing.showPhoneOnListing ?? false,
        });

        if (productTypeId != null && !Number.isNaN(Number(productTypeId))) {
          const specRes = await productTypeService.getSpecConstraints(Number(productTypeId));
          if (!cancelled && specRes.isSuccess && specRes.data) {
            const constraints = normalizeSpecConstraintList(specRes.data);
            setSpecConstraints(constraints);
            setSpecValues(buildSpecValuesFromListing(listing.specs ?? null, constraints));
          }
        }

        if (listing.cityId != null && !Number.isNaN(listing.cityId)) {
          const distRes = await districtService.getAll({ cityId: listing.cityId });
          if (!cancelled && distRes.isSuccess && distRes.data) setDistricts(distRes.data);
        }

        const urls =
          listing.imageUrls?.length
            ? listing.imageUrls
            : (listing.images ?? [])
                .map((img) => (img.url != null && img.url !== '' ? img.url : String(img.id)))
                .filter(Boolean);
        if (!cancelled) {
          setExistingImageUrls(urls);
          setRemovedImageUrls([]);
        }
      } catch (err) {
        if (!cancelled)
          setLoadError(err instanceof Error ? err.message : 'İlan yüklenirken bir hata oluştu.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const secondaryCategoriesForNavigator = useMemo(() => {
    if (!userForm.category) return [];
    const topId = Number(userForm.category);
    if (Number.isNaN(topId)) return [];
    const top = topCategories.find((t) => t.id === topId);
    return (top?.secondaryCategories ?? []).map((s) => ({ id: s.id, name: s.name }));
  }, [topCategories, userForm.category]);

  const productTypesForNavigator = useMemo(() => {
    if (!userForm.category || !userForm.secondaryCategoryId) return [];
    const topId = Number(userForm.category);
    const secId = Number(userForm.secondaryCategoryId);
    if (Number.isNaN(topId) || Number.isNaN(secId)) return [];
    const top = topCategories.find((t) => t.id === topId);
    const sec = top?.secondaryCategories?.find((s) => s.id === secId);
    return sec?.productTypes ?? [];
  }, [topCategories, userForm.category, userForm.secondaryCategoryId]);

  const categoryDraftForNavigator = useMemo<ListingFilterState>(
    () => ({
      ...defaultListingFilterState,
      topCategoryId: userForm.category ? Number(userForm.category) : null,
      secondaryCategoryId: userForm.secondaryCategoryId ? Number(userForm.secondaryCategoryId) : null,
      productTypeId: userForm.productTypeId ? Number(userForm.productTypeId) : null,
    }),
    [userForm.category, userForm.secondaryCategoryId, userForm.productTypeId]
  );

  const isComputerCategory = useMemo(() => {
    if (!userForm.category) return false;
    const topId = Number(userForm.category);
    const top = topCategories.find((t) => t.id === topId);
    return Boolean(top?.name?.toLowerCase().includes('bilgisayar'));
  }, [topCategories, userForm.category]);

  const setListingCategory = useCallback(
    (level: 'top' | 'secondary' | 'productType', catId: number | null) => {
      if (level === 'top') {
        if (catId == null) {
          setFormData((prev) => ({
            ...prev,
            category: '',
            secondaryCategoryId: '',
            productTypeId: '',
          }));
          setSpecConstraints([]);
          setSpecValues({});
          return;
        }
        const top = topCategories.find((t) => t.id === catId);
        const secondaries = top?.secondaryCategories ?? [];
        setSpecConstraints([]);
        setSpecValues({});

        if (secondaries.length === 1) {
          const sec = secondaries[0];
          const types = sec.productTypes ?? [];
          if (types.length === 1) {
            const pt = types[0];
            setFormData((prev) => ({
              ...prev,
              category: String(catId),
              secondaryCategoryId: String(sec.id),
              productTypeId: String(pt.id),
            }));
            void productTypeService.getSpecConstraints(pt.id).then((r) => {
              if (r.isSuccess && r.data) setSpecConstraints(normalizeSpecConstraintList(r.data));
              else setSpecConstraints([]);
            });
          } else {
            setFormData((prev) => ({
              ...prev,
              category: String(catId),
              secondaryCategoryId: String(sec.id),
              productTypeId: '',
            }));
          }
        } else {
          setFormData((prev) => ({
            ...prev,
            category: String(catId),
            secondaryCategoryId: '',
            productTypeId: '',
          }));
        }
        return;
      }

      if (level === 'secondary') {
        setSpecConstraints([]);
        setSpecValues({});
        setFormData((prev) => {
          const topId = prev.category ? Number(prev.category) : NaN;
          if (catId == null || Number.isNaN(topId)) {
            return { ...prev, secondaryCategoryId: '', productTypeId: '' };
          }
          const top = topCategories.find((t) => t.id === topId);
          const secondary = top?.secondaryCategories?.find((s) => s.id === catId);
          const types = secondary?.productTypes ?? [];
          if (types.length === 1) {
            const pt = types[0];
            void productTypeService.getSpecConstraints(pt.id).then((r) => {
              if (r.isSuccess && r.data) setSpecConstraints(normalizeSpecConstraintList(r.data));
              else setSpecConstraints([]);
            });
            return {
              ...prev,
              secondaryCategoryId: String(catId),
              productTypeId: String(pt.id),
            };
          }
          return {
            ...prev,
            secondaryCategoryId: String(catId),
            productTypeId: '',
          };
        });
        return;
      }

      setSpecConstraints([]);
      setSpecValues({});
      if (catId == null) {
        setFormData((prev) => ({ ...prev, productTypeId: '' }));
        return;
      }
      setFormData((prev) => ({ ...prev, productTypeId: String(catId) }));
      void productTypeService.getSpecConstraints(catId).then((r) => {
        if (r.isSuccess && r.data) setSpecConstraints(normalizeSpecConstraintList(r.data));
        else setSpecConstraints([]);
      });
    },
    [topCategories]
  );

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, city: e.target.value, district: '' }));
  };

  const handleDistrictChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, district: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;
    if (!userForm.title || !userForm.price) {
      alert('Lütfen gerekli alanları doldurun');
      return;
    }
    if (!userForm.termsAccepted) {
      alert('Lütfen kullanım koşullarını kabul edin');
      return;
    }
    const productTypeId = userForm.productTypeId;
    if (productTypesForNavigator.length > 0 && !productTypeId) {
      alert('Lütfen ürün tipini seçin.');
      return;
    }

    const city = userForm.city;
    const district = userForm.district;
    const specs: Record<string, string> = {
      ...(userForm.brand && { Marka: userForm.brand }),
      ...(userForm.cpuModel && { 'İşlemci Modeli': userForm.cpuModel }),
      ...(userForm.ram && { RAM: userForm.ram }),
      ...(userForm.storage && { Depolama: userForm.storage }),
      ...(userForm.gpu && { 'Ekran Kartı': userForm.gpu }),
    };
    const specsJson: Record<string, string> = {};
    Object.entries(specValues).forEach(([key, value]) => {
      const trimmedValue = value?.trim();
      if (trimmedValue) specsJson[key] = trimmedValue;
    });

    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('Title', userForm.title);
      form.append('Description', userForm.description ?? '');
      form.append('Price', String(userForm.price));
      form.append('ConditionId', userForm.condition || '');
      form.append('ProductTypeId', productTypeId || '');
      form.append('CityId', city || '');
      form.append('DistrictId', district || '');
      const hasWarrantyFromApi =
        warranties.find((w) => String(w.id) === userForm.warranty)?.value === true;
      form.append(
        'HasWarranty',
        (hasWarrantyFromApi ?? userForm.warranty === 'yes') ? 'true' : 'false'
      );
      form.append('ShowPhoneOnListing', userForm.showPhoneOnListing ? 'true' : 'false');
      if (Object.keys(specs).length > 0) form.append('Specs', JSON.stringify(specs));
      if (Object.keys(specsJson).length > 0)
        form.append('SpecsJson', JSON.stringify(specsJson));
      if (userForm.images?.length) {
        for (let i = 0; i < userForm.images.length; i++) {
          const file = userForm.images[i];
          if (file?.type.startsWith('image/')) form.append('Images', file);
        }
      }
      if (removedImageUrls.length > 0) {
        form.append('RemovedImageUrls', JSON.stringify(removedImageUrls));
      }

      const result = await listingService.update(id, form);
      if (result.isSuccess) {
        alert('İlan güncellendi.');
        navigate('/ilanlarim');
      } else {
        alert(result.message ?? 'İlan güncellenemedi.');
      }
    } catch (err) {
      console.error('İlan güncelleme hatası:', err);
      alert(
        err instanceof Error ? err.message : 'İlan güncellenirken bir hata oluştu'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = () => setShowDeleteConfirm(true);
  const handleCancelDelete = () => setShowDeleteConfirm(false);
  const handleConfirmDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      const result = await listingService.delete(id);
      if (result.isSuccess) {
        setShowDeleteConfirm(false);
        navigate('/ilanlarim');
      } else {
        alert(result.message ?? 'İlan silinemedi.');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'İlan silinirken bir hata oluştu.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container mt-4 text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
          <p className="mt-2 text-muted">İlan yükleniyor...</p>
        </div>
      </>
    );
  }

  if (loadError) {
    return (
      <>
        <Navbar />
        <div className="container mt-4">
          <div className="alert alert-danger">{loadError}</div>
          <Button className="btn btn-outline-secondary" onClick={() => navigate('/ilanlarim')}>
            İlanlarıma Dön
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card shadow-sm">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                  <h2 className="card-title text-center mb-0">İlan Düzenle</h2>
                  <div className="d-flex gap-2">
                    <Button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => navigate('/ilanlarim')}
                    >
                      <i className="fas fa-arrow-left me-2"></i>
                      İlanlarıma Dön
                    </Button>
                    <Button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleDeleteClick}
                      disabled={deleting}
                      loading={deleting}
                    >
                      <i className="fas fa-trash me-2"></i>
                      İlanı Sil
                    </Button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="needs-validation">
                  <div className="card mb-4">
                    <div className="card-body">
                      <h5 className="card-title mb-3">İlan Özellikleri</h5>

                      <div className="property-section border-bottom pb-3 mb-3">
                        <button
                          type="button"
                          className="property-section-header w-100 d-flex justify-content-between align-items-center bg-transparent border-0 p-2"
                          onClick={() => togglePropertySection('categories')}
                        >
                          <h6 className="mb-0 fw-semibold">Kategori Seçimi</h6>
                          <i className={`fas fa-chevron-${propertySections.categories ? 'up' : 'down'} text-primary`} />
                        </button>
                        <div className={`collapse ${propertySections.categories ? 'show' : ''}`}>
                          <div className="category-section mb-2 mt-3">
                            <h6 className="fw-bold border-bottom pb-2 mb-3">Kategoriler</h6>
                            <SlidingCategoryNavigator
                              topCategories={topCategories}
                              secondaryCategories={secondaryCategoriesForNavigator}
                              productTypes={productTypesForNavigator}
                              draft={categoryDraftForNavigator}
                              setDraftCategory={setListingCategory}
                            />
                          </div>
                        </div>
                      </div>

                      {specConstraints.length > 0 && (
                        <div className="property-section border-bottom pb-3 mb-3">
                          <button
                            type="button"
                            className="property-section-header w-100 d-flex justify-content-between align-items-center bg-transparent border-0 p-2"
                            onClick={() => togglePropertySection('specs')}
                          >
                            <h6 className="mb-0 fw-semibold">Ürün Özellikleri</h6>
                            <i className={`fas fa-chevron-${propertySections.specs ? 'up' : 'down'} text-primary`} />
                          </button>
                          <div className={`collapse ${propertySections.specs ? 'show' : ''}`}>
                            <div className="mb-3 mt-3">
                              <SpecInlineFilterPanel
                                constraints={specConstraints}
                                mode="create"
                                specValues={specValues}
                                specMultiValues={{}}
                                specOperators={{}}
                                onSpecValueChange={(key, value) =>
                                  setSpecValues((prev) => ({ ...prev, [key]: value }))
                                }
                                onSpecMultiValueChange={() => {}}
                                onSpecOperatorChange={() => {}}
                                emptyMessage="Bu ürün tipi için tanımlı özellik yok."
                              />
                              <p className="text-muted small mt-2 mb-0">
                                Tüm özellikler opsiyoneldir. Sadece doldurduğunuz alanlar kaydedilecektir.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="property-section border-bottom pb-3 mb-3">
                        <button
                          type="button"
                          className="property-section-header w-100 d-flex justify-content-between align-items-center bg-transparent border-0 p-2"
                          onClick={() => togglePropertySection('condition')}
                        >
                          <h6 className="mb-0 fw-semibold">Ürün Durumu</h6>
                          <i className={`fas fa-chevron-${propertySections.condition ? 'up' : 'down'} text-primary`} />
                        </button>
                        <div className={`collapse ${propertySections.condition ? 'show' : ''}`}>
                          <div className="mb-3 mt-3">
                            <select
                              className="form-select"
                              name="condition"
                              value={userForm.condition}
                              onChange={handleChange}
                              required
                            >
                              <option value="">Seçiniz</option>
                              {conditions.map((c) => (
                                <option key={c.id} value={String(c.id)}>
                                  {c.name ?? `Durum ${c.id}`}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="property-section pb-3 mb-3">
                        <button
                          type="button"
                          className="property-section-header w-100 d-flex justify-content-between align-items-center bg-transparent border-0 p-2"
                          onClick={() => togglePropertySection('warranty')}
                        >
                          <h6 className="mb-0 fw-semibold">Garanti Durumu</h6>
                          <i className={`fas fa-chevron-${propertySections.warranty ? 'up' : 'down'} text-primary`} />
                        </button>
                        <div className={`collapse ${propertySections.warranty ? 'show' : ''}`}>
                          <div className="mb-3 mt-3">
                            <select
                              className="form-select"
                              name="warranty"
                              value={userForm.warranty}
                              onChange={handleChange}
                              required
                            >
                              <option value="">Seçiniz</option>
                              {warranties.map((w) => (
                                <option key={w.id} value={String(w.id)}>
                                  {w.name ?? `Garanti ${w.id}`}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">İlan Başlığı</label>
                    <input
                      type="text"
                      className="form-control"
                      name="title"
                      value={userForm.title}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">Açıklama</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={userForm.description}
                      onChange={handleChange}
                      rows={4}
                      required
                    />
                  </div>

                  <div className="card mb-4 border-0 shadow-sm">
                    <div className="card-body p-4">
                      <h5 className="card-title mb-3 fw-bold">Adres Bilgileri</h5>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">İl</label>
                          <select
                            className="form-select"
                            name="city"
                            value={userForm.city}
                            onChange={handleCityChange}
                            required
                            aria-label="İl seçin"
                          >
                            <option value="">Seçiniz</option>
                            {cities.map((c) => (
                              <option key={c.id} value={String(c.id)}>
                                {c.name ?? `İl ${c.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">İlçe</label>
                          <select
                            className="form-select"
                            name="district"
                            value={userForm.district}
                            onChange={handleDistrictChange}
                            required
                            disabled={!userForm.city}
                            aria-label="İlçe seçin"
                          >
                            <option value="">Seçiniz</option>
                            {districts.map((d) => (
                              <option key={d.id} value={String(d.id)}>
                                {d.name ?? `İlçe ${d.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card mb-4 border-0 shadow-sm">
                    <div className="card-body p-4">
                      {existingImageUrls.filter((url) => !removedImageUrls.includes(url)).length >
                        0 && (
                        <div className="form-group mb-4">
                          <h5 className="card-title mb-3 fw-bold">Mevcut Fotoğraflar</h5>
                          <div className="d-flex flex-wrap gap-3">
                            {existingImageUrls
                              .filter((url) => !removedImageUrls.includes(url))
                              .map((url, index) => (
                                <div
                                  key={`${url}-${index}`}
                                  className="position-relative rounded overflow-hidden border bg-light"
                                  style={{ width: 120, height: 120 }}
                                >
                                  <img
                                    src={MediaService.toImageSrc(url)}
                                    alt={`İlan fotoğrafı ${index + 1}`}
                                    className="w-100 h-100 object-fit-cover"
                                    style={{ objectFit: 'cover' }}
                                    loading="lazy"
                                  />
                                  <Button
                                    type="button"
                                    className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 rounded-circle p-0 d-flex align-items-center justify-content-center"
                                    style={{ width: 28, height: 28 }}
                                    onClick={() => handleRemoveExistingImage(url)}
                                    aria-label="Fotoğrafı kaldır"
                                  >
                                    <i className="fas fa-times" aria-hidden />
                                  </Button>
                                </div>
                              ))}
                          </div>
                          <small className="text-muted d-block mt-2">
                            Bu fotoğraflar ilanınızda yayınlanmaktadır. Kaldırmak için ×
                            butonuna tıklayın. Yeni fotoğraf eklemek için aşağıyı kullanın.
                          </small>
                        </div>
                      )}

                      <div className="form-group mb-4">
                        <h5 className="card-title mb-3 fw-bold">Fotoğraf ve Video Ekle</h5>
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*,video/*"
                          multiple
                          onChange={handleImageChange}
                        />
                        <small className="text-muted">
                          Yeni fotoğraf ekleyebilirsiniz (isteğe bağlı). Mevcut fotoğraflar
                          korunur.
                        </small>
                      </div>

                      <hr className="my-4" />

                      <h5 className="card-title mb-3 fw-bold">Telefon Görünürlüğü</h5>
                      <p className="card-text mb-3">
                        Profilinizde kayıtlı telefon numaranızın bu ilanda gösterilmesini isteyip istemediğinizi seçin.
                      </p>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="showPhoneOnListing"
                          name="showPhoneOnListing"
                          checked={userForm.showPhoneOnListing}
                          onChange={handleCheckboxChange}
                        />
                        <label className="form-check-label" htmlFor="showPhoneOnListing">
                          Telefon numaramı ilanda göster
                        </label>
                      </div>

                      {isComputerCategory && (
                        <>
                      <h5 className="card-title mb-3 fw-bold">
                        Test Edilen Sistem Rozeti Almak İstiyor musunuz?
                      </h5>
                      <p className="card-text mb-3">
                        Test edilen sistem rozeti, bilgisayarınızın temel bileşenlerinin
                        (işlemci, RAM, disk, ekran kartı) test edildiğini ve kararlı çalıştığını
                        gösteren bir işarettir.
                      </p>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="testedSystem"
                          name="testedSystem"
                          checked={userForm.testedSystem}
                          onChange={handleCheckboxChange}
                        />
                        <label className="form-check-label" htmlFor="testedSystem">
                          Evet, sistemimi test etmek ve rozet almak istiyorum
                        </label>
                      </div>
                      <div className="accordion mt-3" id="testDetailsAccordionEdit">
                        <div className="accordion-item border-0 sb-surface-muted rounded mb-2">
                          <h2 className="accordion-header" id="headingOneEdit">
                            <Button
                              className="accordion-button collapsed sb-surface-muted"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseOneEdit"
                              aria-expanded="false"
                              aria-controls="collapseOneEdit"
                            >
                              <strong>1. İşlemci Testi</strong>
                            </Button>
                          </h2>
                          <div
                            id="collapseOneEdit"
                            className="accordion-collapse collapse"
                            aria-labelledby="headingOneEdit"
                            data-bs-parent="#testDetailsAccordionEdit"
                          >
                            <div className="accordion-body">
                              <p>
                                İşlemcinin kararlılığını ve sıcaklığının görmek için CoreTemp ve
                                Cpu-z programı kullanılır.
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="accordion-item border-0 sb-surface-muted rounded mb-2">
                          <h2 className="accordion-header" id="headingTwoEdit">
                            <Button
                              className="accordion-button collapsed sb-surface-muted"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseTwoEdit"
                              aria-expanded="false"
                              aria-controls="collapseTwoEdit"
                            >
                              <strong>2. RAM Testi</strong>
                            </Button>
                          </h2>
                          <div
                            id="collapseTwoEdit"
                            className="accordion-collapse collapse"
                            aria-labelledby="headingTwoEdit"
                            data-bs-parent="#testDetailsAccordionEdit"
                          >
                            <div className="accordion-body">
                              <p>Techpowerup Memtest64 programı ile Windows üzerinde test yapılır.</p>
                            </div>
                          </div>
                        </div>
                        <div className="accordion-item border-0 sb-surface-muted rounded mb-2">
                          <h2 className="accordion-header" id="headingThreeEdit">
                            <Button
                              className="accordion-button collapsed sb-surface-muted"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseThreeEdit"
                              aria-expanded="false"
                              aria-controls="collapseThreeEdit"
                            >
                              <strong>3. HDD/SSD Sağlamlık Testi</strong>
                            </Button>
                          </h2>
                          <div
                            id="collapseThreeEdit"
                            className="accordion-collapse collapse"
                            aria-labelledby="headingThreeEdit"
                            data-bs-parent="#testDetailsAccordionEdit"
                          >
                            <div className="accordion-body">
                              <p>HDD Sentinel programı ile sağlık ve performans değerleri kontrol edilir.</p>
                            </div>
                          </div>
                        </div>
                        <div className="accordion-item border-0 sb-surface-muted rounded">
                          <h2 className="accordion-header" id="headingFourEdit">
                            <Button
                              className="accordion-button collapsed sb-surface-muted"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseFourEdit"
                              aria-expanded="false"
                              aria-controls="collapseFourEdit"
                            >
                              <strong>4. Ekran Kartı Testi</strong>
                            </Button>
                          </h2>
                          <div
                            id="collapseFourEdit"
                            className="accordion-collapse collapse"
                            aria-labelledby="headingFourEdit"
                            data-bs-parent="#testDetailsAccordionEdit"
                          >
                            <div className="accordion-body">
                              <p>Geeks3D Furmark programı ile ekran kartı kararlılık testi yapılır.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">Fiyat (TL)</label>
                    <input
                      type="number"
                      className="form-control"
                      name="price"
                      value={userForm.price}
                      onChange={handleChange}
                      required
                      min="0"
                    />
                  </div>

                  <div className="form-check mb-4 mt-4">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="termsEdit"
                      name="termsAccepted"
                      checked={userForm.termsAccepted}
                      onChange={handleCheckboxChange}
                      required
                    />
                    <label className="form-check-label" htmlFor="termsEdit">
                      <a
                        href="#"
                        className="text-decoration-none"
                        data-bs-toggle="modal"
                        data-bs-target="#termsModalEdit"
                      >
                        İlan Sözleşmesi
                      </a>
                      'ni okudum ve kabul ediyorum
                    </label>
                  </div>

                  <div className="text-center">
                    <Button
                      type="submit"
                      className="btn btn-primary btn-lg w-100"
                      disabled={!userForm.termsAccepted || submitting}
                      loading={submitting}
                    >
                      Değişiklikleri Kaydet
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div
          className="modal fade"
          id="termsModalEdit"
          tabIndex={-1}
          aria-labelledby="termsModalLabelEdit"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="termsModalLabelEdit">
                  İlan Sözleşmesi
                </h5>
                <Button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Kapat"
                ></Button>
              </div>
              <div className="modal-body">
                <h6>SadeceBilgisayar.com İlan Kullanım Koşulları</h6>
                <p>
                  Bu sözleşme, SadeceBilgisayar.com platformunda ilan veren kullanıcılar ile site
                  yönetimi arasındaki hak ve yükümlülükleri belirler.
                </p>
                <h6>1. Genel Kurallar</h6>
                <p>
                  - Kullanıcılar, platformda verdikleri ilanların içeriğinden tamamen kendileri
                  sorumludur.<br />
                  - Telif hakkı ihlali içeren içerikler paylaşılamaz.<br />
                  - Yasadışı ürünlerin satışına yönelik ilanlar yayınlanamaz.
                </p>
                <p className="mb-0">
                  <Link to="/ilan-sozlesmesi" target="_blank" rel="noopener noreferrer" className="text-primary">
                    Tam metni görüntüle
                  </Link>
                </p>
              </div>
              <div className="modal-footer">
                <Button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                  Kapat
                </Button>
              </div>
            </div>
          </div>
        </div>

        {showDeleteConfirm && (
          <div
            className="modal show"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">Onay</h5>
                  <Button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={handleCancelDelete}
                    aria-label="Kapat"
                  ></Button>
                </div>
                <div className="modal-body">
                  <p>İlanı silmek istediğinizden emin misiniz?</p>
                  <p className="text-muted small">Bu işlem geri alınamaz.</p>
                </div>
                <div className="modal-footer">
                  <Button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancelDelete}
                  >
                    İptal
                  </Button>
                  <Button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleConfirmDelete}
                    disabled={deleting}
                    loading={deleting}
                  >
                    Evet, Sil
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Ilan_duzenle;
